import React, { useState, useEffect } from 'react';
import { getBaseUrl } from '../../../shared/utils/apiConfig';
import { usePlatformAdminAuth } from '../contexts/PlatformAdminAuthContext';

/**
 * Platform Settings Page
 * 
 * MIGRATED from tenant dashboard (P-ADMIN-1.3)
 * Original: /src/features/admin/components/system-config/PlatformSettings.tsx
 * 
 * System-wide configuration affecting ALL tenants:
 * - Correlation rules (threshold, time window)
 * - Routing configuration (load balancing, timeout, retries)
 * - Deduplication settings (method, time window)
 * 
 * Security: Requires platform admin authentication
 */

interface CorrelationRules {
  enabled: boolean;
  threshold: number;
  timeWindow: number;
}

interface RoutingConfig {
  loadBalancing: string;
  timeout: number;
  retries: number;
}

interface DeduplicationSettings {
  enabled: boolean;
  method: string;
  timeWindow: number;
}

interface PlatformSettings {
  correlationRules: CorrelationRules;
  routing: RoutingConfig;
  deduplication: DeduplicationSettings;
}

const initialPlatformSettings: PlatformSettings = {
  correlationRules: {
    enabled: true,
    threshold: 75,
    timeWindow: 30
  },
  routing: {
    loadBalancing: 'round-robin',
    timeout: 30,
    retries: 3
  },
  deduplication: {
    enabled: true,
    method: 'fuzzy',
    timeWindow: 60
  }
};

type SettingCategory = keyof PlatformSettings;
type SettingValue = string | number | boolean;

export const PlatformSettings: React.FC = () => {
  const [settings, setSettings] = useState<PlatformSettings>(initialPlatformSettings);
  const [hasChanges, setHasChanges] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const { platformAdmin } = usePlatformAdminAuth();

  useEffect(() => {
    const fetchPlatformSettings = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('astrapeak_platform_admin_token');
        
        const response = await fetch(`${getBaseUrl()}/api/platform-admin/settings`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (!response.ok) {
          throw new Error(`Failed to fetch settings: ${response.statusText}`);
        }
        
        const data = await response.json();
        setSettings(data.settings);
        setError(null);
      } catch (err) {
        console.error('Error fetching platform settings:', err);
        setError('Failed to load platform settings');
        // Fallback to initial settings on error
      } finally {
        setLoading(false);
      }
    };

    fetchPlatformSettings();
  }, []);

  const handleSettingChange = (category: SettingCategory, setting: string, value: SettingValue) => {
    setSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [setting]: value
      }
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('astrapeak_platform_admin_token');
      
      const response = await fetch(`${getBaseUrl()}/api/platform-admin/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ settings })
      });
      
      if (!response.ok) {
        throw new Error(`Failed to save settings: ${response.statusText}`);
      }
      
      setHasChanges(false);
      setError(null);
    } catch (e) {
      console.error('Error saving platform settings:', e);
      setError('Failed to save settings. Please try again.');
    } finally {
      setLoading(false);
      setShowConfirm(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  return (
    <div className="px-4 py-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-semibold text-white">Platform Settings</h2>
          <p className="text-gray-400 mt-1">Configure system-wide platform behavior affecting all tenants</p>
        </div>
        {error && <p className="text-red-400 text-sm">{error}</p>}
      </div>

      {/* Warning Banner */}
      <div className="mb-6 p-4 bg-yellow-900/30 border border-yellow-700/50 rounded-lg">
        <div className="flex items-start space-x-3">
          <svg className="w-5 h-5 text-yellow-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <div>
            <p className="text-sm font-medium text-yellow-200">System-Wide Configuration</p>
            <p className="text-sm text-yellow-300/80 mt-1">Changes to these settings will affect ALL tenants immediately.</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Correlation Rules */}
        <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-6">
          <h3 className="text-white font-semibold text-lg mb-4">Correlation Rules</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <label className="text-sm text-gray-300">Enable Correlation</label>
              <button
                onClick={() => handleSettingChange('correlationRules', 'enabled', !settings.correlationRules.enabled)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  settings.correlationRules.enabled ? 'bg-purple-600' : 'bg-gray-700'
                }`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                  settings.correlationRules.enabled ? 'translate-x-6' : 'translate-x-1'
                }`} />
              </button>
            </div>
            <div>
              <label className="block text-sm text-gray-300 mb-2">Correlation Threshold (%)</label>
              <input
                type="range"
                min="0"
                max="100"
                value={settings.correlationRules.threshold}
                onChange={(e) => handleSettingChange('correlationRules', 'threshold', parseInt(e.target.value))}
                className="w-full accent-purple-500"
              />
              <div className="flex justify-between text-sm text-gray-400 mt-1">
                <span>0%</span>
                <span className="text-purple-400 font-medium">{settings.correlationRules.threshold}%</span>
                <span>100%</span>
              </div>
            </div>
            <div>
              <label className="block text-sm text-gray-300 mb-2">Time Window (minutes)</label>
              <input
                type="number"
                value={settings.correlationRules.timeWindow}
                onChange={(e) => handleSettingChange('correlationRules', 'timeWindow', parseInt(e.target.value))}
                className="w-full bg-gray-900/50 border border-gray-700 rounded px-3 py-2 text-white placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {/* Routing Configuration */}
        <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-6">
          <h3 className="text-white font-semibold text-lg mb-4">Routing Configuration</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm text-gray-300 mb-2">Load Balancing Strategy</label>
              <select
                value={settings.routing.loadBalancing}
                onChange={(e) => handleSettingChange('routing', 'loadBalancing', e.target.value)}
                className="w-full bg-gray-900/50 border border-gray-700 rounded px-3 py-2 text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="round-robin">Round Robin</option>
                <option value="least-connections">Least Connections</option>
                <option value="weighted">Weighted</option>
              </select>
            </div>
            <div>
              <label className="block text-sm text-gray-300 mb-2">Request Timeout (seconds)</label>
              <input
                type="number"
                value={settings.routing.timeout}
                onChange={(e) => handleSettingChange('routing', 'timeout', parseInt(e.target.value))}
                className="w-full bg-gray-900/50 border border-gray-700 rounded px-3 py-2 text-white placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-300 mb-2">Max Retries</label>
              <input
                type="number"
                value={settings.routing.retries}
                onChange={(e) => handleSettingChange('routing', 'retries', parseInt(e.target.value))}
                className="w-full bg-gray-900/50 border border-gray-700 rounded px-3 py-2 text-white placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {/* Deduplication Settings */}
        <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-6">
          <h3 className="text-white font-semibold text-lg mb-4">Deduplication Settings</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <label className="text-sm text-gray-300">Enable Deduplication</label>
              <button
                onClick={() => handleSettingChange('deduplication', 'enabled', !settings.deduplication.enabled)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  settings.deduplication.enabled ? 'bg-purple-600' : 'bg-gray-700'
                }`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                  settings.deduplication.enabled ? 'translate-x-6' : 'translate-x-1'
                }`} />
              </button>
            </div>
            <div>
              <label className="block text-sm text-gray-300 mb-2">Deduplication Method</label>
              <select
                value={settings.deduplication.method}
                onChange={(e) => handleSettingChange('deduplication', 'method', e.target.value)}
                className="w-full bg-gray-900/50 border border-gray-700 rounded px-3 py-2 text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="exact">Exact Match</option>
                <option value="fuzzy">Fuzzy Match</option>
                <option value="semantic">Semantic Match</option>
              </select>
            </div>
            <div>
              <label className="block text-sm text-gray-300 mb-2">Time Window (minutes)</label>
              <input
                type="number"
                value={settings.deduplication.timeWindow}
                onChange={(e) => handleSettingChange('deduplication', 'timeWindow', parseInt(e.target.value))}
                className="w-full bg-gray-900/50 border border-gray-700 rounded px-3 py-2 text-white placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="mt-6 pt-6 border-t border-gray-700 flex justify-end">
        <button
          className={`px-6 py-2.5 rounded-lg text-white font-medium transition-colors ${
            hasChanges && !loading
              ? 'bg-purple-600 hover:bg-purple-700'
              : 'bg-gray-600 cursor-not-allowed'
          }`}
          disabled={!hasChanges || loading}
          onClick={() => setShowConfirm(true)}
        >
          {loading ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      {/* Confirm Dialog */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-gray-800 border border-gray-700 rounded-lg p-6 max-w-md w-full mx-4">
            <h4 className="text-lg font-semibold text-white mb-4">Confirm Save</h4>
            <p className="text-gray-300 mb-6">
              Are you sure you want to save these platform settings? <strong className="text-yellow-400">This will affect all tenants</strong> across the entire platform.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
                onClick={() => setShowConfirm(false)}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-colors"
                onClick={handleSave}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlatformSettings;
